import React, { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';

export const downloadCsv = (filename, columns, rows) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [columns.map(c => esc(c.label)).join(';'), ...rows.map(r => columns.map(c => esc(r[c.key])).join(';'))].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

const TYPE_LABELS = { istanbul: 'İstanbul', yurtici: 'Yurtiçi', yurtdisi: 'Yurtdışı' };

export const DataTable = ({ title, columns, rows, testid, searchKeys = [], typeFilter = false, filename }) => {
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [sort, setSort] = useState({ key: columns.find(c => c.numeric)?.key, dir: -1 });
  const filtered = useMemo(() => {
    let out = rows.filter(r => !q || searchKeys.some(k => String(r[k] || '').toLocaleLowerCase('tr').includes(q.toLocaleLowerCase('tr'))));
    if (type) out = out.filter(r => (type === 'other' ? !r.tour_type : r.tour_type === type));
    if (sort.key) out = [...out].sort((a, b) => ((a[sort.key] ?? -1) > (b[sort.key] ?? -1) ? 1 : -1) * sort.dir);
    return out;
  }, [rows, q, type, sort, searchKeys]);
  return (
    <div className="rounded-2xl bg-white border border-gray-100 p-4" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-bold text-gray-900">{title}</h3>
        <div className="flex flex-wrap gap-2">
          {searchKeys.length > 0 && <Input placeholder="Ara..." value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-44" data-testid={`${testid}-search`} />}
          {typeFilter && (
            <select value={type} onChange={(e) => setType(e.target.value)} className="h-9 border rounded-md px-2 text-sm" data-testid={`${testid}-type-filter`}>
              <option value="">Tüm sayfalar</option>
              <option value="istanbul">İstanbul turları</option>
              <option value="yurtici">Yurtiçi turları</option>
              <option value="yurtdisi">Yurtdışı turları</option>
              <option value="other">Tur dışı sayfalar</option>
            </select>
          )}
          <Button variant="outline" size="sm" onClick={() => downloadCsv(filename, columns, filtered)} data-testid={`${testid}-csv`}><Download size={14} className="mr-1" /> CSV</Button>
        </div>
      </div>
      <div className="overflow-x-auto max-h-96">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600 sticky top-0">
            <tr>
              {columns.map(c => (
                <th key={c.key} className={`p-2 text-left whitespace-nowrap ${c.numeric ? 'cursor-pointer' : ''}`}
                  onClick={() => c.numeric && setSort(s => ({ key: c.key, dir: s.key === c.key ? -s.dir : -1 }))}>
                  {c.label}{sort.key === c.key ? (sort.dir === -1 ? ' ↓' : ' ↑') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td className="p-3 text-gray-500" colSpan={columns.length}>Bu dönemde kayıt yok</td></tr>}
            {filtered.map((r, i) => (
              <tr key={i} className="border-t">
                {columns.map(c => (
                  <td key={c.key} className="p-2 whitespace-nowrap max-w-xs truncate">
                    {c.key === 'tour_type' ? (TYPE_LABELS[r[c.key]] || '–') : (r[c.key] ?? '–')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
